'use client';

import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';

export interface ThemeContextValue {
  primary: string;
  accent: string;
}

const DEFAULT_THEME: ThemeContextValue = {
  primary: '#1D4ED8',
  accent: '#0EA5E9',
};

const ThemeContext = createContext<ThemeContextValue>(DEFAULT_THEME);

export interface ThemeProviderProps {
  primary?: string;
  accent?: string;
  children: ReactNode;
}

export function ThemeProvider({ primary, accent, children }: ThemeProviderProps) {
  const value = useMemo<ThemeContextValue>(
    () => ({
      primary: primary ?? DEFAULT_THEME.primary,
      accent: accent ?? DEFAULT_THEME.accent,
    }),
    [primary, accent],
  );

  useEffect(() => {
    const root = document.documentElement;
    if (primary) root.style.setProperty('--color-brand-primary', primary);
    if (accent) root.style.setProperty('--color-brand-accent', accent);
    return () => {
      if (primary) root.style.removeProperty('--color-brand-primary');
      if (accent) root.style.removeProperty('--color-brand-accent');
    };
  }, [primary, accent]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
